/**
 * Single source of truth for resolving the current portal role.
 *
 * Used by:
 * - `useRole()` hook — authoritative role for routes/components
 * - `Sidebar`, `AppHeader`, `RoleSwitcher` — all consume via `useRole()`
 *
 * Do not duplicate this logic. If you need to decide what portal a user
 * is in, call `useRole()` (or `deriveRole()` if you need it pure/sync).
 */

export type UserRole = "broker" | "employer" | "employee" | "superadmin" | "ga";

export const VALID_ROLES: readonly UserRole[] = [
  "broker",
  "employer",
  "employee",
  "superadmin",
  "ga",
] as const;

function isValidRole(x: unknown): x is UserRole {
  return typeof x === "string" && (VALID_ROLES as readonly string[]).includes(x);
}

/** Read a role hint from the URL (?role=X) or localStorage. Returns null if neither is set/valid. */
export function readRoleHint(): UserRole | null {
  try {
    if (typeof window === "undefined") return null;
    const urlRole = new URLSearchParams(window.location.search).get("role");
    if (isValidRole(urlRole)) return urlRole;
    const saved = localStorage.getItem("selectedRole") || localStorage.getItem("userRole");
    if (isValidRole(saved)) return saved;
  } catch {
    /* ignore */
  }
  return null;
}

/**
 * Resolve the portal role given an authenticated user (or null, pre-auth).
 *
 * Pre-auth: returns the URL/localStorage hint (or "broker" fallback).
 * Post-auth: enforces role constraints based on `user.role` and associations.
 */
export function deriveRole(user: any | null): UserRole {
  const hint = readRoleHint();

  if (!user) {
    return hint ?? "broker";
  }

  // Derive from the ACCOUNT role. `user.role` is the effective portal role
  // the server reports — forced to "employee" while viewing as an employee —
  // and reconciling against it made a broker→employee→broker switch snap
  // back: the stale employee-view user flipped the role to "employee" again
  // before the new fetch landed (the portal ping-pong that blanked the
  // screen for producers, Aug 2026). The URL/localStorage hint carries the
  // user's actual choice; the account role only bounds what is allowed.
  const role = user.baseRole ?? user.role;
  const isSuperAdmin = role === "superadmin";
  const isBroker = role === "broker";
  const isEmployer = role === "employer";
  const isEmployee = role === "employee";
  const isAgencyMember = role === "agency_member";
  const isGa = role === "ga";
  const hasClientId = !!user.clientId;
  const hasEmployeeId = !!user.employeeId;
  const isEmployeeOnly = hasEmployeeId && !hasClientId;

  // GA users always land in the GA portal — no portal switching.
  if (isGa) return "ga";

  if (isSuperAdmin && !user.isImpersonating) {
    // Superadmins may toggle between superadmin ↔ broker portals
    return hint === "broker" ? "broker" : "superadmin";
  }
  if (isSuperAdmin && user.isImpersonating) {
    // Impersonating prevents infinite superadmin loop — force broker
    return "broker";
  }
  if (isBroker) return "broker";
  if (isAgencyMember) {
    // Can switch between broker and employee (if they have an employee record)
    if (hint === "employee" && hasEmployeeId) return "employee";
    return "broker";
  }
  if (isEmployee) return "employee";
  if (isEmployer && hasClientId) {
    // Employers can toggle between employer ↔ employee views
    return hint === "employee" ? "employee" : "employer";
  }
  if (isEmployeeOnly) return "employee";
  return "broker";
}
